import { LinearProgress, Tabs as MuiTabs, Stack, Tab, styled } from "@mui/material";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import { useAtomValue } from "jotai";

import type { Loadable } from "../../utils/jotai-loadable";
import type { ContentTone } from "../brand-kit/server/brand-kit-service";
import type { BrandTone } from "../brand-kit/server/brand-tone-service";
import type { Template } from "../generic/atoms/types/template";
import type { Atom } from "jotai";
import type { FC, MouseEvent } from "react";

export const TemplateTones: FC<{
  loadableTemplateAtom: Atom<Loadable<Template | null>>;
  contentTone: ContentTone | "custom";
  onContentToneChange?: (value: ContentTone | "custom") => void;
  selectedBrandTone?: BrandTone | null;
  customBrandTones?: BrandTone[] | null;
  handleClick: (event: MouseEvent) => void;
}> = ({
  loadableTemplateAtom,
  contentTone,
  onContentToneChange,
  selectedBrandTone,
  customBrandTones,
  handleClick,
}) => {
  const template = useAtomValue(loadableTemplateAtom);

  const ready = template?.state === "hasData" && template.data;

  return (
    <Tabs
      value={contentTone ?? "professional"}
      onChange={(_, v: ContentTone | "custom") => {
        onContentToneChange?.(v);
      }}
      variant="scrollable"
      scrollButtons="auto"
    >
      <Tab
        label={
          <TabLabel
            loading={ready && template.data?.content.professional.state !== "hasData"}
            name="Professional"
          />
        }
        value="professional"
      />
      <Tab
        label={
          <TabLabel
            loading={ready && template.data?.content.casual.state !== "hasData"}
            name="Casual"
          />
        }
        value="casual"
      />
      <Tab
        label={
          <TabLabel
            loading={ready && template.data?.content.direct.state !== "hasData"}
            name="Direct"
          />
        }
        value="direct"
      />
      {selectedBrandTone && (
        <Tab
          sx={{ ml: 3 }}
          label={
            <TabLabel
              loading={ready && template.data?.content.custom?.state !== "hasData"}
              name={selectedBrandTone.name ?? "Custom"}
            />
          }
          value="custom"
          onClick={(e) => {
            if (customBrandTones && customBrandTones.length > 1 && contentTone === "custom")
              handleClick(e);
          }}
          icon={customBrandTones && customBrandTones.length > 1 ? <ArrowDropDownIcon /> : <></>}
          iconPosition="end"
        />
      )}
    </Tabs>
  );
};

const Tabs = styled(MuiTabs)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const TabLabel: FC<{ loading?: boolean | null; name: string }> = ({ loading, name }) => (
  <Stack>
    {name}
    {loading && <LinearProgress color="inherit" />}
  </Stack>
);
